import React, { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { topicRequestsApi } from '../lib/api';
import {
  TOPIC_TITLE_MAX,
  TOPIC_DESCRIPTION_MAX,
  TOPIC_CATEGORIES,
  titleLength,
  descriptionLength,
  validateTopicRequest,
  topicRequestErrorMessage,
} from '../lib/topicRequestValidation';
import PageHero from '../components/layout/PageHero';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Send } from 'lucide-react';

const EMPTY_FORM = { title: '', category: '', description: '' };
const NO_TOUCHED = { title: false, category: false, description: false };

const RequiredMark = () => <span className="text-destructive ml-0.5" aria-hidden="true">*</span>;

const Counter = ({ id, length, max }) => (
  <span id={id} className={`text-xs font-mono ${length > max ? 'text-destructive' : 'text-muted-foreground'}`}>
    {length}/{max}
  </span>
);

const FieldError = ({ id, message }) =>
  message ? <p id={id} className="text-xs text-destructive" role="alert">{message}</p> : null;

const RequestTopicPage = () => {
  const { user } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Errors show for a field once it's been left (blur / select closed), or for every
  // field after a submit attempt -- never while the user is still on their first pass.
  const [touched, setTouched] = useState(NO_TOUCHED);

  const errors = useMemo(() => validateTopicRequest(form), [form]);
  const isValid = Object.keys(errors).length === 0;
  const visibleError = (field) => (touched[field] ? errors[field] : null);

  const touch = (field) => setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setTouched(NO_TOUCHED);
    setSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!isValid) {
      setTouched({ title: true, category: true, description: true });
      return;
    }
    setSubmitting(true);
    try {
      await topicRequestsApi.create({
        title: form.title.trim(),
        category: form.category,
        description: form.description.trim(),
      });
      setSubmitted(true);
      toast.success('Your request has been submitted. A confirmation email is on its way.');
    } catch (err) {
      console.error('Error submitting topic request:', err);
      toast.error(topicRequestErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const titleErr = visibleError('title');
  const categoryErr = visibleError('category');
  const descriptionErr = visibleError('description');

  return (
    <div className="min-h-screen" data-testid="request-topic-page">
      <PageHero
        eyebrow="Resources"
        title="Request a Topic"
        description="Can't find an article, tool, or simulation on something? Tell us what you'd like to see."
      />
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <Card className="max-w-xl mx-auto technical-card">
            <CardContent className="p-6 md:p-8">
              {submitted ? (
                <div className="text-center py-8" data-testid="request-topic-success">
                  <CheckCircle2 className="h-12 w-12 text-autoxium-blue mx-auto mb-4" />
                  <h2 className="font-chivo text-xl font-bold uppercase tracking-tight mb-2">
                    Your request has been submitted.
                  </h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    Thanks for the suggestion — our team reviews requested topics regularly.
                    {user?.email && (
                      <> A confirmation has been sent to <span className="font-medium text-foreground break-all">{user.email}</span>.</>
                    )}
                  </p>
                  <Button
                    variant="outline"
                    className="font-bold uppercase tracking-wider"
                    onClick={resetForm}
                    data-testid="request-topic-another-btn"
                  >
                    Submit Another
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate data-testid="request-topic-form">
                  {user?.email && (
                    <p className="text-xs text-muted-foreground break-words">
                      Submitting as{' '}
                      <span className="font-medium text-foreground">
                        {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}
                      </span>{' '}
                      ({user.email})
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Fields marked <span className="text-destructive">*</span> are required.
                  </p>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="topic-title">Topic Title<RequiredMark /></Label>
                      <Counter id="topic-title-count" length={titleLength(form.title)} max={TOPIC_TITLE_MAX} />
                    </div>
                    <Input
                      id="topic-title"
                      value={form.title}
                      onChange={handleChange('title')}
                      onBlur={() => touch('title')}
                      placeholder="e.g. Understanding UDS security access levels"
                      required
                      aria-required="true"
                      aria-invalid={!!titleErr}
                      aria-describedby={titleErr ? 'topic-title-error topic-title-count' : 'topic-title-count'}
                      className={titleErr ? 'border-destructive focus-visible:ring-destructive' : ''}
                      data-testid="request-topic-title-input"
                    />
                    <FieldError id="topic-title-error" message={titleErr} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="topic-category">Category<RequiredMark /></Label>
                    <Select
                      value={form.category}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, category: value }))}
                      onOpenChange={(open) => { if (!open) touch('category'); }}
                    >
                      <SelectTrigger
                        id="topic-category"
                        aria-required="true"
                        aria-invalid={!!categoryErr}
                        aria-describedby={categoryErr ? 'topic-category-error' : undefined}
                        className={categoryErr ? 'border-destructive focus:ring-destructive' : ''}
                        data-testid="request-topic-category-select"
                      >
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {TOPIC_CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError id="topic-category-error" message={categoryErr} />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="topic-description">Description<RequiredMark /></Label>
                      <Counter
                        id="topic-description-count"
                        length={descriptionLength(form.description)}
                        max={TOPIC_DESCRIPTION_MAX}
                      />
                    </div>
                    <Textarea
                      id="topic-description"
                      rows={5}
                      value={form.description}
                      onChange={handleChange('description')}
                      onBlur={() => touch('description')}
                      placeholder="What should this cover, and why would it help? (at least 20 characters)"
                      required
                      aria-required="true"
                      aria-invalid={!!descriptionErr}
                      aria-describedby={descriptionErr ? 'topic-description-error topic-description-count' : 'topic-description-count'}
                      className={descriptionErr ? 'border-destructive focus-visible:ring-destructive' : ''}
                      data-testid="request-topic-description-input"
                    />
                    <FieldError id="topic-description-error" message={descriptionErr} />
                  </div>

                  <div className="space-y-2">
                    <Button
                      type="submit"
                      disabled={submitting || !isValid}
                      aria-describedby={!isValid ? 'request-topic-submit-hint' : undefined}
                      className="w-full bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider"
                      data-testid="request-topic-submit-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {submitting ? 'Sending...' : 'Send Request'}
                    </Button>
                    {!isValid && (
                      <p id="request-topic-submit-hint" className="text-xs text-muted-foreground text-center">
                        Complete all required fields to send your request.
                      </p>
                    )}
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default RequestTopicPage;
